import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToastController } from 'smoothie';

import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { llmServiceFetcher } from '../LlmServiceFetcher';

interface EvalThreadResponse {
    scorerCount: number;
}

interface EvalAgentThreadInput {
    agentId: string;
    threadId: string;
}

export const useEvalAgentThread = () => {
    const queryClient = useQueryClient();
    const toast = useToastController();

    return useMutation({
        mutationFn: async ({
            agentId,
            threadId,
        }: EvalAgentThreadInput): Promise<EvalThreadResponse> => {
            return await llmServiceFetcher<EvalThreadResponse>({
                endpoint: `/agents/${encodeURIComponent(agentId)}/threads/${encodeURIComponent(threadId)}/eval`,
                method: 'POST',
            });
        },
        onSuccess: async (result, { agentId, threadId }) => {
            // Refetch thread details so the threads table has fresh metadata
            // when navigating back from the detail page.
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: ['thread-scores', threadId],
                    refetchType: 'all',
                }),
                queryClient.invalidateQueries({
                    queryKey: ['mastraMemoryThreads', agentId],
                    refetchType: 'all',
                }),
                queryClient.invalidateQueries({
                    queryKey: ['mastraThread', threadId, agentId],
                    refetchType: 'all',
                }),
            ]);

            if (result.scorerCount === 0) {
                toast.show(
                    'No scorers ran for this thread.',
                    MutationErrorToastOptions('No scorers ran for the thread.'),
                );
                return;
            }

            toast.show(
                `Successfully evaluated thread.`,
                MutationSuccessToastOptions,
            );
        },
        onError: (error) => {
            toast.show(
                'Failed to evaluate thread.',
                MutationErrorToastOptions(error.message),
            );
        },
    });
};
